import React, { useMemo, useState } from "react";

import { formatAmount } from "@/src_new/shared/lib/utils";
import { getInsurerLogo } from "@/src_new/shared/utils/getInsurarLogo";
import {
  getCheckedQuoteAddOnItems,
  getQuoteDisplayTotal,
  isAddonSectionTitle,
} from "@/src_new/shared/utils/quoteAddOns";
import { getNormalizedQuoteSections } from "@/src_new/shared/utils/quoteSections";

import { Button } from "./Button";
import CurrencySymbol from "./QuoteList/CurrencySymbol";
import {
  getQuoteItemPriceValue,
  isPositiveNumberLike,
  shouldShowQuoteItem,
  shouldUseAmountForDisplay,
} from "./QuoteList/quoteDisplay";
import { type CompareQuote, type CompareQuoteItem } from "./compareQuoteData";

const shouldShowItemInCompare = (item?: CompareQuoteItem, sectionTitle?: string) =>
  isAddonSectionTitle(sectionTitle)
    ? Boolean(item?.is_checked) || shouldShowQuoteItem(item)
    : shouldShowQuoteItem(item);

const getQuoteKey = (quote?: CompareQuote) =>
  `${quote?.insurer?.name || ""}_${quote?.plan?.id || ""}_${quote?.plan?.name || ""}_${
    quote?.plan?.price?.amount || ""
  }`;

interface CompareQuoteTableProps {
  quotes: CompareQuote[];
  onBuyNow?: (quote: CompareQuote) => Promise<void> | void;
}

export function CompareQuoteTable({ quotes, onBuyNow }: CompareQuoteTableProps) {
  const [buyingQuoteKey, setBuyingQuoteKey] = useState<string | null>(null);

  const normalizedQuotes = useMemo(
    () =>
      quotes.map((quote) => {
        const selectedAddOns = getCheckedQuoteAddOnItems(quote);
        const sections = getNormalizedQuoteSections(quote?.sections || []);

        return {
          sections: selectedAddOns?.length
            ? [...sections, { title: "Add-ons", items: selectedAddOns }]
            : sections,
        };
      }),
    [quotes]
  );

  const visibleSections = useMemo(
    () =>
      Array.from(
        new Set(normalizedQuotes.flatMap((item) => item.sections.map((section) => section.title)))
      ),
    [normalizedQuotes]
  );

  function getCoverageDisplay(coverage?: CompareQuoteItem, sectionTitle?: string) {
    if (!shouldShowItemInCompare(coverage, sectionTitle)) {
      return "❌";
    }

    if (coverage?.value === true && !isPositiveNumberLike(coverage?.amount)) {
      return (
        <span className="inline-flex items-center rounded-full bg-theme-success-bright-soft px-3 py-1 text-xs font-medium text-theme-success-text-strong">
          ✓ Included
        </span>
      );
    }

    const rawValue =
      shouldUseAmountForDisplay(coverage || {}) ||
      isPositiveNumberLike(getQuoteItemPriceValue(coverage))
        ? getQuoteItemPriceValue(coverage)
        : coverage?.value;

    const isNumeric =
      (typeof rawValue === "number" && !isNaN(rawValue)) ||
      (typeof rawValue === "string" && /^[+-]?\d+(\.\d+)?$/.test(rawValue.trim()));

    const formattedValue = isNumeric ? formatAmount(Number(rawValue), true) : rawValue;

    return (
      <>
        {coverage?.is_checked && (
          <div className="flex justify-center items-center mb-1 text-xs">
            <p className="bg-theme-success-bright-soft rounded-full w-fit px-2 py-1">✓ Selected</p>
          </div>
        )}

        <CurrencySymbol currency={coverage?.currency} value={formattedValue} />
        {formattedValue as React.ReactNode}
      </>
    );
  }

  const callBuyNow = async (quote: CompareQuote) => {
    setBuyingQuoteKey(getQuoteKey(quote));

    try {
      await Promise.resolve(onBuyNow?.(quote));
    } finally {
      setBuyingQuoteKey(null);
    }
  };

  if (quotes.length === 0) {
    return (
      <p className="p-6 text-center text-sm text-theme-text-muted">
        No quotes selected to compare.
      </p>
    );
  }

  return (
    <div className="relative h-[70vh] overflow-y-auto thin-scrollbar border border-theme-border rounded-lg">
      <table className="min-w-full border-collapse text-sm">
        <thead className="bg-theme-surface">
          <tr>
            <th
              className="
        sticky top-0 left-0 z-40
        bg-theme-surface
        px-5 py-4
        text-left text-sm font-semibold text-theme-text
        border-b border-theme-border
      "
            >
              Coverage
            </th>

            {quotes.map((item, idx) => (
              <th
                key={idx}
                className="
          sticky top-0 z-30
          bg-theme-surface
          px-5 py-2
          text-center
          border-b border-theme-border
        "
              >
                <div className="flex h-24 items-center justify-center">
                  <img
                    src={getInsurerLogo(item?.insurer?.name)}
                    alt={item?.insurer?.name}
                    className="max-h-16 w-auto object-contain"
                  />
                </div>
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {visibleSections?.map((sectionTitle) => {
            const sectionItems = Array.from(
              new Set(
                normalizedQuotes?.flatMap(
                  ({ sections }) =>
                    sections
                      ?.find((s) => s.title === sectionTitle)
                      ?.items?.filter((item) =>
                        shouldShowItemInCompare(item as CompareQuoteItem, sectionTitle)
                      )
                      ?.map((item) => item.label)
                      ?.filter((label): label is string => Boolean(label)) || []
                )
              )
            );

            if (sectionItems.length === 0) {
              return null;
            }

            return (
              <React.Fragment key={sectionTitle}>
                <tr className="border border-theme-surface-muted">
                  <td colSpan={quotes.length + 1} className="bg-theme-surface-muted px-6 py-4">
                    <div className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-theme-primary" />
                      <span className="text-sm font-semibold text-theme-text uppercase tracking-wide">
                        {sectionTitle}
                      </span>
                    </div>
                  </td>
                </tr>

                {sectionItems.map((label, rowIdx) => (
                  <tr
                    key={label}
                    className={`border border-theme-border ${
                      rowIdx % 2 === 0 ? "bg-theme-surface" : "bg-theme-surface-muted"
                    }`}
                  >
                    <td className="sticky left-0 bg-inherit px-10 py-3 text-sm text-theme-text-muted">
                      {label}
                    </td>

                    {normalizedQuotes.map(({ sections }, idx) => {
                      const section = sections?.find((s) => s.title === sectionTitle);
                      const coverage = section?.items?.find((it) => it.label === label) as
                        | CompareQuoteItem
                        | undefined;

                      return (
                        <td key={idx} className="px-5 py-3 text-center">
                          <span className="text-sm text-theme-text-soft">
                            {getCoverageDisplay(coverage, sectionTitle)}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
        </tbody>

        <thead className="bg-theme-accent-soft">
          <tr>
            <th
              className="
        sticky bottom-0 left-0 z-50
        bg-theme-accent-soft
        px-5 py-4
        text-left text-sm font-semibold text-theme-text
        border-t border-theme-border-strong
      "
            ></th>

            {quotes.map((item, idx) => (
              <th
                key={idx}
                className="
          sticky bottom-0 z-40
          bg-theme-accent-soft
          px-5 py-4
          text-center text-sm font-semibold text-theme-text
          border-t border-theme-border-strong
        "
              >
                <div className="flex flex-col items-center gap-2">
                  <span className="text-xs text-theme-text-soft">{item?.plan?.name}</span>

                  <div className="flex items-center gap-1 text-lg font-medium text-theme-primary">
                    <CurrencySymbol
                      currency={item.plan?.price?.currency}
                      value={getQuoteDisplayTotal(item)}
                    />{" "}
                    {formatAmount(getQuoteDisplayTotal(item), true)}
                  </div>

                  <Button
                    onClick={() => callBuyNow(item)}
                    isLoading={buyingQuoteKey === getQuoteKey(item)}
                    loadingText="Processing..."
                  >
                    Buy Now
                  </Button>
                </div>
              </th>
            ))}
          </tr>
        </thead>
      </table>
    </div>
  );
}

export default CompareQuoteTable;
